import { useCallback, useMemo, useState } from "react";
import { useUploadFileApi, usePostApi } from "src/hooks";
import { type IFile } from "src/interfaces";
import { type IMedicalPatientInformation } from "../MedicalSections.model";
import { Notification } from "src/utils/Notification";

interface AttachmentConfig {
  uploadEndpoint: string;
  removeEndpoint: string;
  idParam: string;
}

/**
 * Custom hook to manage medical history attachments
 */
export const useAttachmentsModal = (
  entityId: string | null,
  initialAttachments: IFile[] = [],
  config: AttachmentConfig,
  refetch?: () => Promise<void>,
) => {
  const { upload, isUploading } = useUploadFileApi();
  const { post } = usePostApi();
  const [attachments, setAttachments] = useState<IFile[]>(initialAttachments);

  useMemo(() => {
    if (initialAttachments) {
      setAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (isUploading || !entityId) return;

      const body: Record<string, string> = {};
      body[config.idParam] = entityId;

      await upload(
        config.uploadEndpoint,
        files,
        body,
        async res => {
          Notification(
            "Success",
            `The ${files.length > 1 ? "files have" : "file has"} been added successfully`,
            "green",
          );

          if (res?.list) {
            setAttachments(res.list);
          } else if (res?.data) {
            setAttachments(prev => [...prev, res.data]);
          } else if (refetch) {
            await refetch();
          }
        },
        files.length === 1,
      );
    },
    [isUploading, entityId, config, upload, refetch],
  );

  const handleRemove = useCallback(
    async (attachmentId: string) => {
      if (isUploading || !entityId) return;

      const params: Record<string, string> = {
        aid: attachmentId,
      };
      params[config.idParam] = entityId;

      await post({
        request: config.removeEndpoint,
        body: { params },
        onSuccess: () => {
          setAttachments(prev =>
            prev.filter(file => String(file.aid) !== attachmentId),
          );
          Notification(
            "Success",
            "File has been removed successfully",
            "green",
          );
        },
      });
    },
    [isUploading, entityId, config, post],
  );

  return {
    entityId,
    attachments,
    isUploading,
    handleUpload,
    handleRemove,
  };
};

export const useMedicalDocumentsModal = (
  patientInformation: IMedicalPatientInformation,
  refetch: () => Promise<void>,
) => {
  const config: AttachmentConfig = {
    uploadEndpoint: "upload-med-attachment",
    removeEndpoint: "remove-med-attachment",
    idParam: "patientid",
  };

  return useAttachmentsModal(
    patientInformation?.patientId || null,
    patientInformation?.attachments || [],
    config,
    refetch,
  );
};

export const useVaccineAttachments = (
  vaxid: string | null,
  initialAttachments: any[] = [],
  refetch?: () => Promise<void>,
) => {
  const config = {
    uploadEndpoint: "upload-vax-attachment",
    removeEndpoint: "remove-vax-attachment",
    idParam: "vaxid",
  };

  return useAttachmentsModal(vaxid, initialAttachments, config, refetch);
};
